import React, { ReactElement } from 'react';
import { connect } from 'react-redux';
import { Routes, Route, Navigate } from 'react-router';
import ErrorLayout from '../../../components/error-layout/error-layout.component';
import ContentContainer from '../../../components/content-container/content-container.component';
import Dashboard from '../../dashboard/dashboard.container';
import AssetList from '../../asset-list/asset-list.container';
import Asset from '../../asset/asset.container';
import ActivityList from '../../activity-list/activity-list.container';
import Activity from '../../activity/activity.container';
import DealList from '../../deal-list/deal-list.container';
import Deal from '../../deal/deal.container';
import ContactList from '../../contact-list/contact-list.container';
import Contact from '../../contact/contact.container';
import ReportList from '../../report-list/report-list.container';
import Report from '../../report/report.container';
import Search from '../../search/search.container';
import Settings from '../../settings/settings.container';
import SettingsAccount from '../../settings/settings-account/settings-account.container';
import SettingsAccountList from '../../settings/settings-account-list/settings-account-list.container';
import SettingsOperationLog from '../../settings/settings-operation-log/settings-operation-log.container';
import SettingsDealTemplateList from '../../settings/settings-deal-template-list/settings-deal-template-list.container';
import SettingsPortfolioGroupList
  from '../../settings/settings-portfolio-group-list/settings-portfolio-group-list.container';
import SettingsUserList from '../../settings/settings-user-list/settings-user-list.container';
import SettingsProfile from '../../settings/settings-profile/settings-profile.container';
import SettingsSecurity from '../../settings/settings-security/settings-security.container';
import SettingsNotifications from '../../settings/settings-notifications/settings-notifications.container';
import SettingsUser from '../../settings/settings-user/settings-user.container';
import SettingsPortfolioGroup from '../../settings/settings-portfolio-group/settings-portfolio-group.container';
import SettingsPortfolio from '../../settings/settings-portfolio/settings-portfolio.container';
import SettingsDealTemplate from '../../settings/settings-deal-template/settings-deal-template.container';
import NotFound from './not-found/not-found.container';
import ErrorFallback from '../error-fallback/error-fallback.component';
import Logout from './logout/logout.container';
import AuthService from '../../../services/auth-service';
import {
  ROUTE_ACTIVITY_LIST, ROUTE_ASSET_LIST, ROUTE_CONTACT_LIST, ROUTE_DASHBOARD, ROUTE_DEAL_LIST, ROUTE_LOGIN,
  ROUTE_LOGOUT, ROUTE_SEARCH, ROUTE_SETTINGS, ROUTE_USER, ROUTE_REPORT_LIST, ROUTE_PARAM_ID, ROUTE_CONTACT, ROUTE_DEAL,
  ROUTE_REPORT, ROUTE_ACTIVITY, ROUTE_ASSET, ROUTE_NOT_FOUND, ROUTE_PORTFOLIO_GROUP, ROUTE_DEAL_TEMPLATE,
  ROUTE_PORTFOLIO, ROUTE_SETTINGS_ACCOUNT, ROUTE_SETTINGS_ACCOUNT_LIST, ROUTE_SETTINGS_DEAL_TEMPLATE_LIST,
  ROUTE_SETTINGS_PORTFOLIO_GROUP_LIST, ROUTE_SETTINGS_OPERATION_LOG, ROUTE_SETTINGS_USER_LIST,
  ROUTE_SETTINGS_PROFILE, ROUTE_SETTINGS_SECURITY, ROUTE_SETTINGS_NOTIFICATIONS, ROUTE_ALL,
} from '../../../constants/system/routes';

const Content: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const {
    isEditMode,
    isLoading,
    isDataLoaded,
    isRightDrawerOpen,
    isServerFault,
    currentUser,
  } = props;

  const storedAuthToken: string | null = AuthService.getAuthToken();
  const isNoUserDataLoaded: boolean = isDataLoaded && (!currentUser || !currentUser.id);
  const isLoadBlocked: boolean = !isDataLoaded && !isLoading && !storedAuthToken;

  if (!isServerFault && (isNoUserDataLoaded || isLoadBlocked)) {
    return <Navigate to={ROUTE_LOGIN} />;
  }

  return (
    <ErrorLayout
      component={ErrorFallback}
      componentProps={{
        isFullWidth: !isRightDrawerOpen,
      }}
    >
      <ContentContainer isEditMode={isEditMode} isCompressed={isRightDrawerOpen}>
        <Routes>
          <Route path={ROUTE_LOGOUT} element={<Logout />} />
          <Route path={ROUTE_DASHBOARD} element={<Dashboard />} />
          <Route path={ROUTE_SETTINGS} element={<Settings />} />
          <Route path={ROUTE_SETTINGS_ACCOUNT} element={<SettingsAccount />} />
          <Route path={ROUTE_SETTINGS_ACCOUNT_LIST} element={<SettingsAccountList />} />
          <Route path={ROUTE_SETTINGS_OPERATION_LOG} element={<SettingsOperationLog />} />
          <Route path={ROUTE_SETTINGS_DEAL_TEMPLATE_LIST} element={<SettingsDealTemplateList />} />
          <Route path={ROUTE_SETTINGS_PORTFOLIO_GROUP_LIST} element={<SettingsPortfolioGroupList />} />
          <Route path={ROUTE_SETTINGS_USER_LIST} element={<SettingsUserList />} />
          <Route path={ROUTE_SETTINGS_PROFILE} element={<SettingsProfile />} />
          <Route path={ROUTE_SETTINGS_SECURITY} element={<SettingsSecurity />} />
          <Route path={ROUTE_SETTINGS_NOTIFICATIONS} element={<SettingsNotifications />} />
          <Route path={ROUTE_SEARCH} element={<Search />} />
          <Route path={ROUTE_ASSET_LIST} element={<AssetList />} />
          <Route path={ROUTE_ACTIVITY_LIST} element={<ActivityList />} />
          <Route path={ROUTE_DEAL_LIST} element={<DealList />} />
          <Route path={ROUTE_CONTACT_LIST} element={<ContactList />} />
          <Route path={ROUTE_REPORT_LIST} element={<ReportList />} />
          <Route path={`${ROUTE_DEAL_TEMPLATE}/:${ROUTE_PARAM_ID}`} element={<SettingsDealTemplate />} />
          <Route path={`${ROUTE_PORTFOLIO_GROUP}/:${ROUTE_PARAM_ID}`} element={<SettingsPortfolioGroup />} />
          <Route path={`${ROUTE_PORTFOLIO}/:${ROUTE_PARAM_ID}`} element={<SettingsPortfolio />} />
          <Route path={`${ROUTE_USER}/:${ROUTE_PARAM_ID}`} element={<SettingsUser />} />
          <Route path={`${ROUTE_ASSET}/:${ROUTE_PARAM_ID}`} element={<Asset />} />
          <Route path={`${ROUTE_ACTIVITY}/:${ROUTE_PARAM_ID}`} element={<Activity />} />
          <Route path={`${ROUTE_DEAL}/:${ROUTE_PARAM_ID}`} element={<Deal />} />
          <Route path={`${ROUTE_CONTACT}/:${ROUTE_PARAM_ID}`} element={<Contact />} />
          <Route path={`${ROUTE_REPORT}/:${ROUTE_PARAM_ID}`} element={<Report />} />
          <Route path={ROUTE_NOT_FOUND} element={<NotFound />} />
          <Route path={ROUTE_ALL} element={<Navigate replace to={ROUTE_NOT_FOUND} />} />
        </Routes>
      </ContentContainer>
    </ErrorLayout>
  );
});

const mapState: ReduxMapStateCallback = (reducers: Reducers): ReduxMapState => {
  const {
    app,
    controls,
    rightDrawer,
  } = reducers;

  return {
    isRightDrawerOpen: rightDrawer.isDrawerOpen,
    isEditMode: controls.isEditMode,
    isServerFault: app.isServerFault,
    isDataLoaded: !!app.baseData,
    isLoading: app.isLoading,
    currentUser: app.baseData && app.baseData.currentUser,
  };
};

export default connect(mapState)(Content);
